import axios from "axios";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { server2 } from "../../constant/config";
import Spinner from "../Spinner";
import Layout from "../../layout/Layout";
import MarkdownRenderer from "../MarkDown";
import FormattingToolbar from "../FormattingToolBar";
import { useAuthConfig } from "../../hooks/useAuthConfig";
import CurriculumSelector from "../CurriculumSelector";

const EditMtp = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const boardId = searchParams.get("board");
  const subjectId = searchParams.get("subject");
  const gradeId = searchParams.get("class");
  const number = searchParams.get("number");
  const [payload, setPayload] = useState({
    board_id: boardId || "",
    class_id: gradeId || "",
    subject_id: subjectId || "",
    year: "2026",
  });
  const handleCurriculumChange = useCallback((selections) => {
    setPayload((prev) => ({
      ...prev,
      board_id: selections.board || "",
      class_id: selections.class || "",
      subject_id: selections.subject || "",
    }));
  }, []);
  
  const [data, setData] = useState("");
  const textareaRef = useRef(null);

  const [extra, setExtra] = useState({
    error: null,
    loading: false,
    saving: false,
  });
  const config = useAuthConfig();

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchData = async () => {
      setExtra((p) => ({ ...p, loading: true }));
      try {
        const res = await axios.get(`${server2}/v1/mtpai/${id}`, config);
        const mtp = res?.data?.mtp_ai || {};
        setData(mtp.paper || "");
        setPayload((prev) => ({
          ...prev,
          board_id: mtp.board_id ? String(mtp.board_id) : prev.board_id,
          class_id: mtp.class_id ? String(mtp.class_id) : prev.class_id,
          subject_id: mtp.subject_id ? String(mtp.subject_id) : prev.subject_id,
          year: mtp.year ? String(mtp.year) : prev.year,
        }));
        setExtra((p) => ({ ...p, loading: false, error: null }));
      } catch (err) {
        setExtra((p) => ({
          ...p,
          error: err.message,
          loading: false,
        }));
      }
    };

    if (id) fetchData();
  }, [id]);

  /* ================= SAVE ================= */
  const handleSave = async () => {
    setExtra((p) => ({ ...p, saving: true, error: null }));
    try {
      await axios.patch(
        `${server2}/v1/mtpai/${id}`,
        {
          paper: data,
          board_id: payload.board_id ? Number(payload.board_id) : undefined,
          class_id: payload.class_id ? Number(payload.class_id) : undefined,
          subject_id: payload.subject_id ? Number(payload.subject_id) : undefined,
          year: payload.year ? Number(payload.year) : undefined,
        },
        config
      );

      setExtra((p) => ({ ...p, saving: false }));
      alert("MTP saved successfully!");
    } catch (err) {
      console.log(err);
      setExtra((p) => ({
        ...p,
        error: err.message,
        saving: false,
      }));
    }
  };

  if (extra.loading) return <Spinner />;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <h1 className="text-2xl font-semibold text-gray-800">
            Edit MTP {number || ""}
          </h1>
        <div className="flex  items-center justify-start gap-4 mb-4">
          

         
            <CurriculumSelector
              key={`${payload.board_id}-${payload.class_id}-${payload.subject_id}`}
              initialSelections={{
                board: payload.board_id,
                class: payload.class_id,
                subject: payload.subject_id,
              }}
              onChange={handleCurriculumChange}
            />

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700" htmlFor="mtp-year">
                Year
              </label>
              <input
                id="mtp-year"
                type="number"
                min="1900"
                max="2100"
                value={payload.year}
                onChange={(e) =>
                  setPayload((prev) => ({ ...prev, year: e.target.value }))
                }
                disabled={extra.saving}
                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

           
         
        </div>

        {/* Error */}
        {extra.error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-red-600">
            {extra.error}
          </div>
        )}

        {/* Editor */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200">
          {/* Toolbar */}
          <div className="border-b p-2 bg-gray-50 rounded-t-xl">
            <FormattingToolbar
              textareaRef={textareaRef}
              value={data}
              onChange={setData}  
            />
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={data}
            onChange={(e) => setData(e.target.value)} 
            disabled={extra.saving}
            placeholder="Start writing your paper here..."
            className="w-full min-h-[400px] p-4 resize-y
                       text-gray-800 text-sm leading-relaxed
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Preview */}
        <div className="mt-8 ">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">
            Live Preview
          </h2>

          <div className="bg-white rounded-xl shadow-md border p-6 h-[400px] resize-y overflow-y-scroll  ">
            <MarkdownRenderer content={data} />
          </div>
        </div>
        <button
              onClick={handleSave}
              disabled={extra.saving}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium
                         hover:bg-blue-700 disabled:opacity-50 transition mt-2"
            >
              {extra.saving ? "Saving..." : "Save"}
            </button>
      </div>
    </Layout>
  );
};

export default EditMtp;
